import React, { useEffect, useMemo, useRef } from 'react';
import Navbar from './Navbar';
import Hero from './sections/Hero';
import Marquee from './sections/Marquee';
import Calendly from './sections/Calendly';
import Services from './sections/Services';
import BeforeAfter from './sections/BeforeAfter';
import Process from './sections/Process';
import Receipts from './sections/Receipts';
import Compare from './sections/Compare';
import FitCheck from './sections/FitCheck';
import Faq from './sections/Faq';
import Cta from './sections/Cta';
import Footer from './sections/Footer';
import Floating from './sections/Floating';

function StarField() {
  // ~80 tiny twinkling dots; positions/durations/delays randomized once
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 80; i++) {
      arr.push({
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() < 0.5 ? 1 : 2,
        duration: 2 + Math.random() * 3,    // 2 → 5s
        delay: Math.random() * 5,
      });
    }
    return arr;
  }, []);
  return (
    <div className="star-field" aria-hidden="true">
      {stars.map((s, i) => (
        <span
          key={i}
          className="star-dot"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function CursorGlow() {
  const ref = useRef(null);
  const target = useRef({ x: -300, y: -300 });
  const current = useRef({ x: -300, y: -300 });
  const rafRef = useRef(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return; // hide on touch devices
    const onMove = (e) => {
      target.current = { x: e.clientX, y: e.clientY };
    };
    const tick = () => {
      // lerp
      current.current.x += (target.current.x - current.current.x) * 0.12;
      current.current.y += (target.current.y - current.current.y) * 0.12;
      if (ref.current) {
        ref.current.style.transform = `translate3d(${current.current.x - 150}px, ${current.current.y - 150}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    window.addEventListener('mousemove', onMove);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return <div ref={ref} className="cursor-glow" aria-hidden="true" />;
}

export default function Site() {
  useEffect(() => {
    document.title = 'JustICP';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    // Click ripple effect on all primary buttons (delegated)
    const onClickRipple = (e) => {
      const btn = e.target.closest('.btn-primary');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'btn-ripple';
      const size = Math.max(rect.width, rect.height);
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
      btn.appendChild(ripple);
      setTimeout(() => ripple.remove(), 700);
    };
    document.addEventListener('mousedown', onClickRipple);

    // Parallax on orbs (translateY scales with scroll)
    const orbs = document.querySelectorAll('.page-ambient .orb');
    const onScroll = () => {
      const y = window.scrollY * -0.2;
      orbs.forEach((o) => {
        o.style.setProperty('--scroll-y', `${y}px`);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mousedown', onClickRipple);
    };
  }, []);

  return (
    <>
      <div className="page-ambient" aria-hidden="true">
        <div className="mesh-gradient" />
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="orb orb-4" />
        <StarField />
        <div className="scanlines" />
      </div>
      <CursorGlow />
      <div className="noise" aria-hidden="true" />
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Calendly />
        <Services />
        <BeforeAfter />
        <Process />
        <Receipts />
        <Compare />
        <FitCheck />
        <Faq />
        <Cta />
      </main>
      <Footer />
      <Floating />
    </>
  );
}
